import { z } from "zod";
import { responseSchema } from "../http.js";
/**
 * El estándar de la casa: la caja de un frasco de suplemento. Es el
 * valor que viaja a Trasso cuando el producto no declara el suyo.
 */
export const STANDARD_PRODUCT_SHIPPING = Object.freeze({
    weightGrams: 500,
    lengthCm: 15,
    widthCm: 10,
    heightCm: 10,
});
/** La guía se pide este porcentaje MÁS grande que la suma calculada. */
export const SHIPPING_GUIDE_BUFFER_PERCENT = 20;
/**
 * Las dimensiones de UN producto, campo por campo: lo que declaró, y el
 * estándar en cada hueco. Campo por campo a propósito — un producto con
 * peso real pero sin medidas usa SU peso y las medidas estándar.
 */
export function shippingOf(product) {
    const declared = product.shipping;
    return {
        weightGrams: declared?.weightGrams ?? STANDARD_PRODUCT_SHIPPING.weightGrams,
        lengthCm: declared?.lengthCm ?? STANDARD_PRODUCT_SHIPPING.lengthCm,
        widthCm: declared?.widthCm ?? STANDARD_PRODUCT_SHIPPING.widthCm,
        heightCm: declared?.heightCm ?? STANDARD_PRODUCT_SHIPPING.heightCm,
    };
}
/**
 * Lo que un PAQUETE puede declarar de su caja real. Sin `requiresShipping`:
 * un paquete siempre se envía. Los cuatro campos son opcionales por la
 * misma razón que en el producto — cada hueco lo llena la suma de sus
 * partes (`shippingOfPackage`), y ninguna ficha incompleta detiene un
 * envío.
 */
export const packageShippingSchema = z.object({
    weightGrams: z.number().int().min(0).optional(),
    lengthCm: z.number().min(0).optional(),
    widthCm: z.number().min(0).optional(),
    heightCm: z.number().min(0).optional(),
});
/**
 * Las dimensiones de UN paquete, campo por campo: lo que declaró y, en
 * cada hueco, la SUMA de sus productos (la caja exacta, sin margen — el
 * margen del 20 % se aplica una sola vez, sobre la orden entera).
 *
 * Existe porque un paquete de 6 frascos NO viaja en 6 cajas de frasco:
 * viaja en una caja real que alguien ya midió. Cuando nadie la midió,
 * la suma es la mejor aproximación que hay.
 */
export function shippingOfPackage(declared, items) {
    const summed = estimatePackageShipping(items).exact;
    return {
        weightGrams: declared?.weightGrams ?? summed.weightGrams,
        lengthCm: declared?.lengthCm ?? summed.lengthCm,
        widthCm: declared?.widthCm ?? summed.widthCm,
        heightCm: declared?.heightCm ?? summed.heightCm,
    };
}
/**
 * La estimación de envío de un PAQUETE (o de cualquier pedido): el peso
 * es la suma de los pesos; la caja sale del volumen sumado, repartido en
 * una caja de proporciones parecidas a la estándar. Sobre las dos cosas
 * va el margen del 20 %.
 *
 * La caja derivada es una APROXIMACIÓN honesta, no un empaquetado
 * óptimo: resolver el acomodo real de N frascos es un problema que no
 * le toca a este contrato. Para pedir una guía basta con que el volumen
 * no quede corto — de eso se encargan la suma y el margen.
 */
export function estimatePackageShipping(items) {
    let weightGrams = 0;
    let volumeCm3 = 0;
    let units = 0;
    for (const { product, quantity } of items) {
        const dims = shippingOf(product);
        weightGrams += dims.weightGrams * quantity;
        volumeCm3 += dims.lengthCm * dims.widthCm * dims.heightCm * quantity;
        units += quantity;
    }
    /*
     * De volumen a caja: se conservan las PROPORCIONES de la caja
     * estándar (15×10×10) y se escalan hasta cubrir el volumen sumado.
     * Así una caja de 6 frascos se parece a una caja real de 6 frascos,
     * no a un cubo abstracto.
     */
    const std = STANDARD_PRODUCT_SHIPPING;
    const stdVolume = std.lengthCm * std.widthCm * std.heightCm;
    const scale = volumeCm3 > 0 ? Math.cbrt(volumeCm3 / stdVolume) : 0;
    const exact = {
        weightGrams: Math.ceil(weightGrams),
        lengthCm: Math.ceil(std.lengthCm * scale),
        widthCm: Math.ceil(std.widthCm * scale),
        heightCm: Math.ceil(std.heightCm * scale),
    };
    return { exact, guide: withGuideBuffer(exact), units };
}
/**
 * Unas dimensiones con el margen del 20 % con que se PIDE la guía.
 *
 * El margen del peso va completo; el de cada LADO es la raíz cúbica
 * del margen, para que el VOLUMEN de la guía —que es lo que cobra la
 * paquetería— crezca exactamente el 20 % y no el 72 % (1.2³).
 *
 * Es pública porque un bulto ÚNICO con caja declarada (un paquete
 * medido, una unidad) no debe pasar por la suma: la suma lo volvería a
 * meter en las proporciones estándar y una caja de 30 cm saldría de 24.
 */
export function withGuideBuffer(exact) {
    const buffer = 1 + SHIPPING_GUIDE_BUFFER_PERCENT / 100;
    const sideBuffer = Math.cbrt(buffer);
    return {
        weightGrams: Math.ceil(exact.weightGrams * buffer),
        lengthCm: Math.ceil(exact.lengthCm * sideBuffer),
        widthCm: Math.ceil(exact.widthCm * sideBuffer),
        heightCm: Math.ceil(exact.heightCm * sideBuffer),
    };
}
/**
 * ═══════════════════════════════════════════════════════════
 * La integración con Trasso (27-ago-2026)
 *
 * Trasso vende guías de envío por API: se COTIZA (gratis, la dirección
 * completa viaja desde la cotización), se elige una opción y se GENERA
 * la guía — el precio sale de lo que Trasso guardó al cotizar, nunca de
 * lo que mande este sistema. La cotización vence a los 30 minutos y
 * genera UNA sola guía (esa es la idempotencia: no hay cabecera aparte).
 *
 * ── Dónde vive cada cosa ───────────────────────────────────
 * El flujo es del PANEL: con el pago validado, la orden cotiza y genera
 * su guía desde el detalle. La orden guarda la guía como FOTO
 * (`orderShipmentSchema`); el estado lo mueven el webhook firmado y el
 * botón de actualizar. El PDF de la etiqueta NUNCA se guarda: Trasso lo
 * entrega en una URL firmada que caduca en 1 hora y trae los datos del
 * destinatario — se pide fresco cada vez que alguien lo va a abrir.
 *
 * ── El dinero ──────────────────────────────────────────────
 * Trasso manda `price_mxn` como `number` en la cotización y como
 * `string` en la guía. Aquí TODO se convierte a centavos enteros en el
 * borde (regla de la casa): ninguna aritmética con flotantes.
 * ═══════════════════════════════════════════════════════════
 */
// ─── La dirección estructurada ───────────────────────────────────────
/**
 * Lo que una guía necesita del destino, campo por campo — Trasso exige
 * calle, ciudad, estado y CP de 5 dígitos por separado; una dirección en
 * texto libre no se puede cotizar. Es la MISMA forma que captura el
 * checkout: así la guía se genera sin que nadie reescriba nada.
 */
export const shippingAddressSchema = z.object({
    street: z
        .string()
        .trim()
        .min(5, "Escribe calle, número y colonia")
        .max(200, "Esa dirección es demasiado larga"),
    city: z.string().trim().min(2, "Escribe la ciudad").max(80),
    /**
     * Nombre o abreviatura — «Jalisco» o «JAL» —; Trasso lo normaliza de
     * su lado. Texto libre a propósito: un pedido en USD puede ir a un
     * estado que no es de México, y ese error lo reporta la cotización
     * con su porqué, no un candado nuestro.
     */
    state: z.string().trim().min(2, "Escribe el estado").max(60),
    zip: z
        .string()
        .trim()
        .regex(/^\d{5}$/, "El código postal son 5 dígitos"),
    reference: z.string().trim().max(160).optional(),
});
/** Los 32 estados, para sugerir en el formulario (Trasso acepta ambas formas). */
export const MX_STATES = [
    "Aguascalientes", "Baja California", "Baja California Sur", "Campeche",
    "Chiapas", "Chihuahua", "Ciudad de México", "Coahuila", "Colima",
    "Durango", "Estado de México", "Guanajuato", "Guerrero", "Hidalgo",
    "Jalisco", "Michoacán", "Morelos", "Nayarit", "Nuevo León", "Oaxaca",
    "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí", "Sinaloa",
    "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán",
    "Zacatecas",
];
/** Quién manda o recibe: la dirección más el nombre y el teléfono. */
export const shipmentPartySchema = shippingAddressSchema.extend({
    name: z.string().trim().min(1, "Falta el nombre").max(120),
    phone: z
        .string()
        .trim()
        .min(7, "Escribe un teléfono de contacto")
        .max(30),
});
// ─── Estados de la guía ──────────────────────────────────────────────
/** Los estados de Trasso, tal cual los reporta (§4 de su contrato). */
export const SHIPMENT_STATUSES = [
    "label_created",
    "picked_up",
    "in_transit",
    "out_for_delivery",
    "delivered",
    "exception",
    "returned",
    "cancelled",
];
export const shipmentStatusSchema = z.enum(SHIPMENT_STATUSES);
// ─── Cotizar ─────────────────────────────────────────────────────────
/** Las dimensiones como viajan por el API: enteros positivos, en gramos
 * y centímetros — las unidades de `ShippingDimensions`. */
export const shippingDimensionsSchema = z.object({
    weightGrams: z.number().int().min(1).max(500_000),
    lengthCm: z.number().int().min(1).max(500),
    widthCm: z.number().int().min(1).max(500),
    heightCm: z.number().int().min(1).max(500),
});
/**
 * Lo que el panel manda para cotizar el envío de UNA orden. El origen
 * viene prellenado de la SEDE elegida y el destino de la orden, pero los
 * dos son editables — por eso viajan completos. El paquete es opcional:
 * sin él, el servidor lo calcula de los productos de la orden
 * (`estimatePackageShipping`, con su margen del 20 %).
 */
export const shippingQuoteRequestSchema = z.object({
    from: shipmentPartySchema,
    to: shipmentPartySchema,
    package: shippingDimensionsSchema.optional(),
    /** La sede de origen, solo para dejarla anotada en la guía. */
    sedeId: z.string().trim().min(1).optional(),
});
/**
 * Una opción de servicio, ya con el precio en CENTAVOS. Vienen ordenadas
 * por conveniencia (Trasso pondera precio, tiempo y confiabilidad):
 * la primera es su recomendación, pero se puede elegir cualquiera.
 */
export const shippingQuoteOptionSchema = z.object({
    carrier: z.string(),
    serviceCode: z.string(),
    priceMxnCents: z.number().int().min(0),
    etaDays: z.number().int().min(0).optional(),
});
export const shippingQuoteResponseSchema = responseSchema(z.object({
    quoteId: z.string(),
    options: z.array(shippingQuoteOptionSchema),
    /** El paquete que se cotizó: la suma con su margen, o el override. */
    package: shippingDimensionsSchema,
    /** Cuándo vence (30 minutos): la pantalla lo avisa, el servidor lo exige. */
    expiresAt: z.string(),
}));
// ─── Generar, consultar y cancelar ───────────────────────────────────
/** Generar la guía: la cotización elegida y nada más — el precio y la
 * dirección salen de lo que Trasso guardó al cotizar. */
export const createShipmentRequestSchema = z.object({
    quoteId: z.string().trim().min(1, "Cotiza primero"),
    serviceCode: z.string().trim().min(1, "Elige el servicio"),
});
/**
 * La guía como vive EN la orden: una foto de lo generado más el estado
 * que el webhook va moviendo. Sin `labelUrl` a propósito — esa URL
 * firmada caduca en 1 hora y trae datos personales: se pide fresca con
 * `SHIPPING_ROUTES.adminLabel` en el momento de abrirla.
 */
export const orderShipmentSchema = z.object({
    shipmentId: z.string(),
    carrier: z.string(),
    serviceCode: z.string(),
    /** Puede venir `null` si la paquetería aún no lo asigna. */
    trackingNumber: z.string().nullable().optional(),
    status: shipmentStatusSchema,
    priceMxnCents: z.number().int().min(0),
    from: shipmentPartySchema,
    to: shipmentPartySchema,
    package: shippingDimensionsSchema,
    sedeId: z.string().optional(),
    sedeName: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string().optional(),
    /**
     * `true` = Trasso reembolsó al cancelar, pero la etiqueta FÍSICA puede
     * seguir viva con la paquetería: hay que destruir la impresa. Viene de
     * `live_label_on_cancel` y se guarda para que el aviso no se pierda.
     */
    liveLabelOnCancel: z.boolean().optional(),
});
/** La URL firmada del PDF, fresca: vive 1 hora desde este instante. */
export const shipmentLabelResponseSchema = responseSchema(z.object({ url: z.string() }));
// ─── Wallet ──────────────────────────────────────────────────────────
/**
 * El saldo prepagado en Trasso: cada guía se descuenta de aquí, y una
 * guía que falla por saldo (`402 insufficient_balance`) es una venta
 * detenida. El panel lo enseña junto al flujo de envío para que la
 * recarga llegue ANTES del 402.
 */
export const shippingWalletResponseSchema = responseSchema(z.object({
    currency: z.string(),
    availableCents: z.number().int(),
    updatedAt: z.string().optional(),
}));
// ─── Rutas ───────────────────────────────────────────────────────────
export const SHIPPING_ROUTES = {
    /** Cotizar el envío de una orden (guarda el borrador en la orden). */
    adminQuote: (orderId) => `/api/admin/orders/${orderId}/shipping/quote`,
    /** Generar la guía con la cotización elegida. */
    adminCreate: (orderId) => `/api/admin/orders/${orderId}/shipping`,
    /** La URL fresca del PDF de la etiqueta. */
    adminLabel: (orderId) => `/api/admin/orders/${orderId}/shipping/label`,
    /** Consultar el estado en Trasso ahora mismo (el rastreo se actualiza
     * cada 30 min de su lado; esto trae lo último que tengan). */
    adminRefresh: (orderId) => `/api/admin/orders/${orderId}/shipping/refresh`,
    /** Cancelar la guía: reembolsa el importe completo al wallet. */
    adminCancel: (orderId) => `/api/admin/orders/${orderId}/shipping/cancel`,
    adminWallet: "/api/admin/shipping/wallet",
    /**
     * La conciliación: repregunta a Trasso por todas las guías vivas.
     *
     * Existe porque sus webhooks tienen UN solo intento: un despliegue a
     * destiempo pierde el evento y la guía se queda congelada en nuestro
     * panel. Su documentación lo dice y recomienda conciliar a diario.
     */
    adminReconcile: "/api/admin/shipping/reconcile",
    /** El receptor de eventos de Trasso: firma HMAC sobre el cuerpo crudo. */
    webhook: "/api/webhooks/trasso",
};
/**
 * ═══════════════════════════════════════════════════════════
 * Las CAJAS de envío y el envío en el checkout (13-oct-2026)
 *
 * (Anahí, 13-oct: «subir los tamaños de cada producto y el tamaño en
 * cajas para activar la API de envíos de Trasso, y agregar los envíos
 * en el sistema».)
 *
 * ── Las cajas ──────────────────────────────────────────────
 * El producto declara lo que mide SU frasco (la ficha técnica). Un
 * pedido no viaja en frascos sueltos: viaja en una de las cajas que la
 * bodega tiene de verdad. El panel captura ese catálogo y el sistema
 * elige la MÁS CHICA donde cabe el pedido (`pickShippingBox`); esa
 * caja —sus medidas y su peso vacía más el de los frascos— es lo que
 * se cotiza. Sin cajas capturadas, o si el pedido no cabe en ninguna,
 * manda la suma por volumen de siempre: ningún catálogo incompleto
 * detiene un envío.
 *
 * ── El envío lo paga quien compra ──────────────────────────
 * El checkout cotiza con Trasso en cuanto hay dirección, el comprador
 * elige el servicio y su precio entra al TOTAL del pedido. El precio es
 * una foto que guarda el servidor (`quoteId`): del navegador solo viaja
 * qué cotización y qué servicio, nunca un monto. El envío NO es venta:
 * no genera puntos ni comisiones — las compras salen de los renglones.
 * ═══════════════════════════════════════════════════════════
 */
/**
 * Una caja de la bodega. Medidas INTERIORES —lo que cabe— en cm, y lo
 * que pesa vacía en gramos. `active: false` la saca de la elección sin
 * borrarla (se acabaron, vuelven la semana que entra).
 */
export const shippingBoxSchema = z.object({
    name: z.string().trim().min(1, "Ponle nombre a la caja").max(60),
    lengthCm: z.number().positive("Escribe el largo").max(200),
    widthCm: z.number().positive("Escribe el ancho").max(200),
    heightCm: z.number().positive("Escribe el alto").max(200),
    emptyWeightGrams: z.number().int().min(0).max(20_000),
    active: z.boolean(),
});
/**
 * La configuración de envíos: de qué sede salen los pedidos de la
 * tienda (el origen que se cotiza en el checkout) y las cajas.
 */
export const shippingSettingsInputSchema = z
    .object({
    originSedeId: z.string().trim().min(1).nullable(),
    boxes: z.array(shippingBoxSchema).max(20, "Máximo 20 cajas"),
})
    .superRefine((value, ctx) => {
    const seen = new Set();
    value.boxes.forEach((box, index) => {
        const key = box.name.trim().toLowerCase();
        if (seen.has(key)) {
            ctx.addIssue({
                code: "custom",
                path: ["boxes", index, "name"],
                message: "Ya hay una caja con ese nombre",
            });
        }
        seen.add(key);
    });
});
export const shippingSettingsSchema = z.object({
    originSedeId: z.string().nullable(),
    boxes: z.array(shippingBoxSchema),
    updatedAt: z.string().optional(),
});
export const shippingSettingsResponseSchema = responseSchema(z.object({ settings: shippingSettingsSchema }));
/**
 * Qué tanto de una caja se puede llenar: el resto es relleno y el
 * acomodo imperfecto de frascos redondos en una caja cuadrada.
 */
export const SHIPPING_BOX_FILL_PERCENT = 80;
const sortedSides = (d) => [d.lengthCm, d.widthCm, d.heightCm].sort((a, b) => b - a);
/**
 * La caja más chica (por volumen) donde cabe el pedido: el volumen de
 * los bultos no pasa del `SHIPPING_BOX_FILL_PERCENT` de la caja, y el
 * bulto más grande entra lado por lado (girado como haga falta). `null`
 * si no hay cajas activas o si no cabe en ninguna — ahí el llamador
 * vuelve a la suma por volumen.
 *
 * Es una aproximación honesta, como la suma: decidir el acomodo exacto
 * de N frascos no le toca al contrato. Lo que sí garantiza es que nunca
 * se cotiza una caja donde el pedido no puede entrar por volumen.
 */
export function pickShippingBox(units, boxes) {
    const real = units.filter((unit) => unit.quantity > 0);
    if (real.length === 0)
        return null;
    let volume = 0;
    let weight = 0;
    for (const { dims, quantity } of real) {
        volume += dims.lengthCm * dims.widthCm * dims.heightCm * quantity;
        weight += dims.weightGrams * quantity;
    }
    const biggest = real
        .map((unit) => sortedSides(unit.dims))
        .reduce((acc, sides) => [
        Math.max(acc[0], sides[0]),
        Math.max(acc[1], sides[1]),
        Math.max(acc[2], sides[2]),
    ]);
    const fill = SHIPPING_BOX_FILL_PERCENT / 100;
    const fits = boxes
        .filter((box) => box.active)
        .filter((box) => {
        const sides = sortedSides(box);
        return (sides[0] >= biggest[0] &&
            sides[1] >= biggest[1] &&
            sides[2] >= biggest[2] &&
            box.lengthCm * box.widthCm * box.heightCm * fill >= volume);
    })
        .sort((a, b) => a.lengthCm * a.widthCm * a.heightCm - b.lengthCm * b.widthCm * b.heightCm);
    const [box] = fits;
    if (!box)
        return null;
    return {
        box,
        package: {
            weightGrams: Math.ceil(weight + box.emptyWeightGrams),
            lengthCm: Math.ceil(box.lengthCm),
            widthCm: Math.ceil(box.widthCm),
            heightCm: Math.ceil(box.heightCm),
        },
    };
}
// ─── El envío en el checkout ─────────────────────────────────────────
/**
 * Cotizar el envío de lo que se va a comprar. `items` son productos o
 * paquetes (por id, como los renglones de la orden). Con `storeSlug` no
 * se cotiza: la tienda de un franquiciatario entrega por su cuenta.
 */
export const checkoutShippingQuoteRequestSchema = z.object({
    currency: z.enum(["MXN", "USD"]),
    items: z
        .array(z.object({
        productId: z.string().trim().min(1),
        quantity: z.number().int().min(1).max(99),
    }))
        .min(1)
        .max(50),
    shippingAddress: shippingAddressSchema,
    fullName: z.string().trim().max(120).optional(),
    phone: z.string().trim().max(30).optional(),
    storeSlug: z.string().trim().min(1).optional(),
});
/** Una opción para el comprador: precio en centavos de la moneda del pedido (MXN). */
export const checkoutShippingOptionSchema = z.object({
    carrier: z.string(),
    serviceCode: z.string(),
    priceCents: z.number().int().min(0),
    etaDays: z.number().int().min(0).optional(),
});
/**
 * Por qué un pedido NO cobra envío en el checkout:
 *  · `not_shippable` — no lleva nada físico (una membresía digital).
 *  · `store`         — tienda de franquiciatario: entrega quien vende.
 *  · `usd`           — Trasso solo cubre México; el envío se coordina.
 *  · `unavailable`   — Trasso sin llave o sin sede de origen: se coordina
 *                      a mano, y la venta no se detiene por eso.
 */
export const CHECKOUT_SHIPPING_SKIP_REASONS = [
    "not_shippable",
    "store",
    "usd",
    "unavailable",
];
export const checkoutShippingQuoteSchema = z.discriminatedUnion("required", [
    z.object({
        required: z.literal(true),
        quoteId: z.string(),
        currency: z.literal("MXN"),
        options: z.array(checkoutShippingOptionSchema),
        package: shippingDimensionsSchema,
        boxName: z.string().optional(),
        expiresAt: z.string(),
    }),
    z.object({
        required: z.literal(false),
        reason: z.enum(CHECKOUT_SHIPPING_SKIP_REASONS),
    }),
]);
export const checkoutShippingQuoteResponseSchema = responseSchema(z.object({ quote: checkoutShippingQuoteSchema }));
/** Lo que el pedido manda: QUÉ cotización y QUÉ servicio. Nunca el monto. */
export const checkoutShippingSelectionSchema = z.object({
    quoteId: z.string().trim().min(1),
    serviceCode: z.string().trim().min(1),
});
/**
 * El envío que PAGÓ el comprador, como foto en la orden. Sirve al panel
 * para generar la guía con el mismo servicio y para cuadrar lo cobrado
 * contra lo que Trasso descuenta del wallet.
 */
export const orderShippingChargeSchema = z.object({
    carrier: z.string(),
    serviceCode: z.string(),
    priceCents: z.number().int().min(0),
    etaDays: z.number().int().min(0).optional(),
    package: shippingDimensionsSchema,
    boxName: z.string().optional(),
    quotedAt: z.string(),
});
export const SHIPPING_SETTINGS_ROUTES = {
    admin: "/api/admin/shipping/settings",
    checkoutQuote: "/api/orders/shipping-quote",
};
//# sourceMappingURL=shipping.js.map